/**
 * Marks a selection change as an explicit removal — a chip's remove button, or
 * Backspace on a highlighted chip.
 *
 * react-aria reports every selection change through one `onChange`, so from the
 * new value alone "the user removed this chip" and "react-aria toggled this
 * option off because the user pressed Enter on it" are indistinguishable, and
 * {@link useCreatableCombobox} has to treat those two opposite intents
 * differently. react-stately calls `onChange` synchronously from `setValue`, so
 * a flag held across that one call reaches the handler and nothing else.
 */
let removalDepth = 0;

export const withExplicitRemoval = (removeSelection: () => void) => {
  removalDepth += 1;
  try {
    removeSelection();
  } finally {
    removalDepth -= 1;
  }
};

/** Only meaningful inside a combobox `onChange`. */
export const isExplicitRemoval = () => removalDepth > 0;

/**
 * The same question for the other unambiguous removal: pressing the option that
 * is already selected, which its checkmark advertises as a toggle. That one
 * reaches `onChange` through react-aria rather than through the wrapper above,
 * so what is recorded instead is which kind of input the user last used — a
 * press in the popup, or a key in the field. Enter on a selected option and a
 * click on it are the same change, and only the click says "remove it".
 */
let isPointerInput = false;

/** For the popup listbox's `onPointerDownCapture`. */
export const notePointerInput = () => {
  isPointerInput = true;
};

/** For the field input's `onKeyDownCapture`. */
export const noteKeyboardInput = () => {
  isPointerInput = false;
};

/** Only meaningful inside a combobox `onChange`. */
export const isPointerRemoval = () => isPointerInput;
